import { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Ticket, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { nextSeminarDate, seminarDateLabel } from "../lib/seminarDate";

const useCountdown = () => {
  const [target] = useState(nextSeminarDate);
  const [left, setLeft] = useState(target.getTime() - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(target.getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  const total = Math.max(0, Math.floor(left / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    mins: Math.floor((total % 3600) / 60),
    secs: total % 60,
  };
};

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <div
      className="font-display w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center text-2xl sm:text-3xl font-bold"
      style={{ background: "var(--bg-card)", border: "1px solid rgba(14, 217, 181, 0.25)", color: "var(--accent)", boxShadow: "0 0 24px rgba(14, 217, 181, 0.12)" }}
    >
      {String(value).padStart(2, "0")}
    </div>
    <span className="mt-2 text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</span>
  </div>
);

const details = [
  { icon: Calendar, text: seminarDateLabel() },
  { icon: Clock, text: "11:00 AM IST" },
  { icon: MapPin, text: "Online (Live on Zoom)" },
  { icon: Ticket, text: "Free Entry" },
];

export const SeminarHero = ({ onCta }) => {
  const { days, hours, mins, secs } = useCountdown();
  return (
    <section className="relative overflow-hidden" style={{ background: "var(--bg-primary)" }} data-testid="seminar-hero">
      <div className="hero-glow" />
      <div className="relative max-w-4xl mx-auto px-5 pt-16 pb-16 sm:pt-24 sm:pb-24 text-center">
        <Reveal>
          <span
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase"
            style={{ color: "var(--accent)", border: "1px solid rgba(14, 217, 181, 0.3)", background: "rgba(14, 217, 181, 0.06)" }}
            data-testid="seminar-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--accent)" }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "var(--accent)" }} />
            </span>
            Live Seminar · Limited Seats
          </span>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.12] text-white" data-testid="seminar-headline">
            The Truth About Building a Tech Career in{" "}
            <span style={{ color: "var(--accent)" }}>AI, Cybersecurity & Data Science</span> — In 2026
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-body)" }} data-testid="seminar-subheadline">
            A free live seminar for 12th pass-outs, college students, and graduates who want real clarity — not more
            confusion — about starting a high-paying tech career.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-testid="seminar-details-row">
            {details.map((d) => (
              <span key={d.text} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-body)" }}>
                <d.icon size={15} style={{ color: "var(--accent)" }} />
                {d.text}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={400}>
          <div className="mt-10 flex items-center justify-center gap-3 sm:gap-4" data-testid="seminar-countdown">
            <TimeBox value={days} label="Days" />
            <TimeBox value={hours} label="Hours" />
            <TimeBox value={mins} label="Mins" />
            <TimeBox value={secs} label="Secs" />
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-10">
            <button
              data-testid="seminar-hero-cta-button"
              onClick={onCta}
              className="cta-button inline-flex items-center gap-2 rounded-full px-8 py-4 text-base sm:text-lg font-bold"
            >
              Reserve My Free Seat
              <ArrowRight size={20} />
            </button>
            <p className="mt-4 text-sm" style={{ color: "var(--accent)" }} data-testid="seminar-micro-line">
              Only 37 seats left for this batch.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
